import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Package, Receipt, RefreshCw, Copy, ArrowLeftRight, Clock, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import bit3 from "@/components/icons/bits/bit-3blade-render.png";
import bit4 from "@/components/icons/bits/bit-4blade-render.png";
import bit5 from "@/components/icons/bits/bit-5blade-render.png";
import bit6 from "@/components/icons/bits/bit-6blade-render.png";
import bitTricone from "@/components/icons/bits/bit-tricone-render.png";
import { LiveWake } from "@/components/LiveIndicator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 12000;   // match the app's existing live cadence
const SHOW_MAX = 4;      // rows when expanded

// Bit identity (family render + label + size + thread chip) — same vocabulary as
// the Activity tab. The activity txns don't carry these, so the strip joins
// against the warehouse's bits by marking.
const FAMILY_IMG = { "3 Blade": bit3, "4 Blade": bit4, "5 Blade": bit5, "6 Blade": bit6, "Tricone": bitTricone };
const FAMILY_LABEL = { "Tricone": "VRS / Tricone" };
const famLabel = (f) => FAMILY_LABEL[f] || f;
function bitFamily(b) {
  if (!b) return "";
  const ptype = String(b.product_type || b.body_type || "").toUpperCase();
  const blades = String(b.blades || "");
  if (ptype.includes("TRICONE") || blades.toUpperCase().includes("CONE")) return "Tricone";
  const m = blades.match(/^\s*(\d+)\s*Blade\s*$/i);
  if (m) return `${m[1]} Blade`;
  return "Other";
}
function FmtFrac({ children }) {
  const s = String(children ?? "");
  if (!s) return null;
  const parts = s.split(/(\d+\/\d+)/);
  const dig = { fontSize: "0.64em", lineHeight: 1 };
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^(\d+)\/(\d+)$/);
        if (!m) return <React.Fragment key={i}>{p}</React.Fragment>;
        return (
          <span key={i} style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}>
            <span style={{ ...dig, transform: "translateY(-0.30em)" }}>{m[1]}</span>
            <span style={{ fontSize: "0.95em", opacity: 0.5, margin: "0 -0.03em" }}>⁄</span>
            <span style={{ ...dig, transform: "translateY(0.16em)" }}>{m[2]}</span>
          </span>
        );
      })}
    </>
  );
}
// Inline, compact identity for a strip line: family thumb + "5 Blade · 8-½"" in
// muted text, thread in the blue chip. Renders nothing if the bit isn't known.
function BitIdentity({ bit }) {
  if (!bit) return null;
  const family = bitFamily(bit);
  const label = famLabel(family);
  const size = bit.bit_size_in || (bit.bit_size_mm ? `${bit.bit_size_mm} mm` : "");
  const thread = (bit.thread_size || "").trim();
  const img = FAMILY_IMG[family];
  if (!label && !size && !thread) return null;
  return (
    <span className="inline-flex items-center gap-1.5 align-[-1px] shrink-0">
      {img && <img src={img} alt="" className="w-3.5 h-3.5 object-contain shrink-0" draggable={false} aria-hidden="true" />}
      {(label || size) && (
        <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">{label}{label && size ? " · " : ""}{size && <FmtFrac>{size}</FmtFrac>}</span>
      )}
      {thread && (
        <span className="text-[10px] text-[#185FA5] dark:text-[#85B7EB] bg-[#185FA5]/10 border border-[#185FA5]/30 rounded-full px-1.5 py-0 inline-flex items-center whitespace-nowrap shrink-0">
          <FmtFrac>{thread}</FmtFrac>
        </span>
      )}
    </span>
  );
}

// Condensed type vocab (mirrors StockCenterActivity; the strip is a one-liner so
// it needs only disc + verb).
const TYPE_META = {
  receive:   { verb: "Received",       icon: Package,        color: "#185FA5" },
  sell:      { verb: "Sold",           icon: Receipt,        color: "#0F6E56" },
  status:    { verb: "Status changed", icon: RefreshCw,      color: "#BA7517" },
  duplicate: { verb: "Duplicated",     icon: Copy,           color: "#5F5E5A" },
  transfer:  { verb: "Transferred",    icon: ArrowLeftRight, color: "#534AB7" },
};
const typeMeta = (t) => TYPE_META[t] || { verb: t || "Updated", icon: Clock, color: "#5F5E5A" };

function relTime(iso) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function detailOf(t) {
  if (t.type === "sell" && t.customer) return `to ${t.customer}`;
  if (t.type === "status" && (t.old_status || t.new_status)) return `${t.old_status || "—"} → ${t.new_status || "—"}`;
  if (t.type === "transfer" && (t.from_warehouse || t.to_warehouse)) return `${t.from_warehouse || "—"} → ${t.to_warehouse || "—"}`;
  return "";
}

function Disc({ type, size = 18 }) {
  const m = typeMeta(type);
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center justify-center rounded-full shrink-0"
          style={{ width: size, height: size, background: `${m.color}1a`, color: m.color }} aria-hidden="true">
      <Icon style={{ width: size * 0.62, height: size * 0.62 }} />
    </span>
  );
}

// Marking → white outlined "Part#" pill; design → recessed grey code chip.
// Same treatment as the stock Table, so a bit reads identically everywhere.
function MarkingPill({ children }) {
  return <span className="font-mono-data text-[11px] px-1.5 py-0.5 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill font-semibold text-[hsl(var(--foreground))]">{children}</span>;
}
function DesignPill({ children }) {
  return <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]">{children}</span>;
}

// One condensed event line: a FIXED head (disc · verb · marking pill) that always
// stays legible, then a TRUNCATING tail (family·size · thread · buyer · user) that
// clips with an ellipsis when the row is narrow — so nothing ever overlaps the
// time. The tail is a flex row with overflow hidden + min-w-0 on each child.
function Line({ t, bit }) {
  const m = typeMeta(t.type);
  const detail = detailOf(t);
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      {/* Head — never shrinks; the "what + which bit". */}
      <Disc type={t.type} size={18} />
      <span className="text-[13px] font-semibold shrink-0" style={{ color: m.color }}>{m.verb}</span>
      {t.marking_no && <MarkingPill>{t.marking_no}</MarkingPill>}
      {/* Tail — truncates as one unit. */}
      <span className="flex items-center gap-1.5 min-w-0 overflow-hidden text-[12.5px]">
        <BitIdentity bit={bit} />
        {detail && <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">{detail}</span>}
        {t.user ? <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">· {t.user.split(/\s+/)[0]}</span> : null}
      </span>
    </span>
  );
}

// One event row — every row shares this layout so they align into one column:
//   [leading slot] · disc+content (compresses/truncates) · time · [trailing slot]
// The leading slot holds the live blip on the top row and an equal-width empty
// spacer on the rest; the trailing slot holds the expand chevron on the top row
// and an equal-width spacer on the rest. Keeps the single-row footprint while
// the discs/pills/times stay in clean columns when expanded.
function EventRowBtn({ t, bit, flash, onClick, trailing }) {
  const interactive = !!onClick;
  // The top row is the interactive expand toggle (a <button>); the expanded rows
  // are display-only (a plain <div> — no hover, no cursor, not clickable).
  const Tag = interactive ? "button" : "div";
  // No leading slot — the live dot lives in the section eyebrow now, freeing the
  // full row width. Content truncates cleanly; the time + chevron are shrink-0
  // anchors on the right that the content can never overrun.
  const inner = (
    <>
      <span className={`min-w-0 flex-1 overflow-hidden transition-opacity duration-500 ${flash ? "opacity-40" : "opacity-100"}`}>
        <Line t={t} bit={bit} />
      </span>
      <span className="text-[11px] text-[hsl(var(--muted-foreground))] shrink-0 whitespace-nowrap ml-1" title={new Date(t.timestamp).toLocaleString()}>{relTime(t.timestamp)}</span>
      {trailing}
    </>
  );
  return (
    <Tag
      {...(interactive ? { type: "button", onClick } : {})}
      className={`w-full flex items-center gap-2 px-3 py-2 text-left ${interactive ? "hover:bg-[hsl(var(--muted))]/30 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]" : ""}`}>
      {inner}
    </Tag>
  );
}

// Live indicator: green + pulsing when the latest event is UNSEEN; grey + static
// once the user has seen it. The ping halo only renders in the unseen state.
function LiveBlip({ seen }) {
  return (
    <LiveWake
      color={seen ? "#9A9A93" : "#0F6E56"}
      idle={seen}
      title={seen ? "No new activity" : "New activity"}
    />
  );
}

// Slim live activity strip — sits above the Browse cards. A header bar (live dot
// + label + expand control) sits above a uniform list: the latest event always
// shows; expanding reveals a few more + a link to the full tab. Polls quietly on
// the app cadence, pauses while the tab is hidden. New top item fades in.
export default function StockCenterLiveStrip({ activeWh, onViewAll, onOpenBit }) {
  const [items, setItems] = useState([]);
  const [bitMap, setBitMap] = useState({});
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  // The id of the most recent event the user has already seen (persisted), so
  // the dot is green only for a genuinely-new top event and grey once seen —
  // and stays grey across reloads.
  const SEEN_KEY = "rl_sc_live_seen";
  const [seenId, setSeenId] = useState(() => { try { return localStorage.getItem(SEEN_KEY) || ""; } catch { return ""; } });
  const topIdRef = useRef(null);
  const bitKey = (wh, mk) => `${wh || ""}|${String(mk || "")}`;
  const bitFor = useCallback((t) => bitMap[bitKey(t.warehouse, t.marking_no)], [bitMap]);

  const fetchLatest = useCallback(async () => {
    try {
      const whq = activeWh && activeWh !== "ALL" ? `?warehouse=${activeWh}` : "";
      const [actRes, bitsRes] = await Promise.all([
        axios.get(`${API}/stock-center/activity?limit=20`),
        axios.get(`${API}/stock-center/bits${whq}`),
      ]);
      let data = actRes.data || [];
      if (activeWh && activeWh !== "ALL") data = data.filter((t) => t.warehouse === activeWh);
      data = data.slice(0, SHOW_MAX);
      const map = {};
      (bitsRes.data || []).forEach((b) => { if (b.marking_no) map[bitKey(b.warehouse, b.marking_no)] = b; });
      setBitMap(map);
      setItems(data);
      // Flash the leading row when a genuinely new event arrives (id changed).
      const newTop = data[0]?.id || null;
      if (topIdRef.current && newTop && newTop !== topIdRef.current) {
        setFlash(true);
        setTimeout(() => setFlash(false), 1200);
      }
      topIdRef.current = newTop;
    } catch (e) { /* strip is ambient — stay quiet on errors */ }
  }, [activeWh]);

  // Initial load + poll, paused when the tab isn't visible.
  useEffect(() => {
    let timer = null;
    const tick = () => { if (!document.hidden) fetchLatest(); };
    fetchLatest();
    timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (!document.hidden) fetchLatest(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (timer) clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchLatest]);

  // Mark the current top event "seen" a moment after it's shown — the dot reads
  // green briefly (so a genuinely new event registers), then settles to grey and
  // persists, so it won't re-green on reload until something newer arrives.
  const topId = items[0]?.id || "";
  useEffect(() => {
    if (!topId || topId === seenId) return;
    const t = setTimeout(() => {
      setSeenId(topId);
      try { localStorage.setItem(SEEN_KEY, topId); } catch {}
    }, 2500);
    return () => clearTimeout(t);
  }, [topId, seenId]);

  // Nothing to show → render nothing (no empty strip cluttering the top).
  if (!items.length) return null;

  const top = items[0];
  const rest = items.slice(1);
  const Chevron = open ? ChevronDown : ChevronRight;
  const canExpand = rest.length > 0;
  const unseen = top.id !== seenId;  // green+pulse while unseen, grey once seen

  return (
    <div className="mb-4" data-testid="sc-live-strip">
      {/* Section eyebrow — matches "PICK A BIT TYPE". The live dot rides here (left
          of the label), so it no longer indents/crowds the row content. */}
      <div className="flex items-center gap-1.5 mb-1.5 ml-0.5">
        <LiveBlip seen={!unseen} />
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]/70">Latest</span>
      </div>
      <div className="rl-glass-edge rounded-xl bg-[hsl(var(--card))] overflow-hidden">
      {/* Top row — single line: latest event · time · expand chevron. The chevron
          sits in a small white disc so row text can't visually run into it. The
          only interactive row: clicking it toggles the rest open. */}
      <EventRowBtn
        t={top} bit={bitFor(top)} flash={flash}
        onClick={canExpand ? () => setOpen((o) => !o) : undefined}
        trailing={canExpand ? (
          <span className="shrink-0 ml-0.5 w-6 h-6 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] inline-flex items-center justify-center">
            <Chevron className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" aria-hidden="true" />
          </span>
        ) : null}
      />
      {/* Expanded rows — DISPLAY ONLY (not clickable). */}
      {open && rest.map((t) => (
        <div key={t.id} className="border-t border-[hsl(var(--border))]/60">
          <EventRowBtn t={t} bit={bitFor(t)} trailing={null} />
        </div>
      ))}
      {open && (
        <button type="button" onClick={() => onViewAll?.()}
          className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#0F6E56] dark:text-[#5DCAA5] border-t border-[hsl(var(--border))]/70 hover:bg-[#0F6E56]/[0.05] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]"
          data-testid="sc-live-viewall">
          View all activity <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
      </div>
    </div>
  );
}
